import React from 'react';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

const RemittanceFinancialCharts = ({ ownerReport, trendData, expenseBreakdown }) => {
    const COLORS = {
        primary: '#FF5733',
        secondary: '#2a2e30',
        blue: '#3b82f6',
        green: '#10b981',
        yellow: '#f59e0b',
        purple: '#8b5cf6',
        red: '#ef4444',
        pink: '#ec4899'
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(value);
    };

    // Chart 1: Income breakdown per car (WKI + WKR)
    const incomeData = ownerReport?.cars?.map(car => ({
        name: car.plate_no,
        WKI: parseFloat(car.wki_total || 0),
        WKR: parseFloat(car.wkr_total || 0),
        Charges: parseFloat(car.expenses_total || 0)
    })) || [];

    // Chart 2: Expense distribution by category
    const expenseData = [];
    if (expenseBreakdown?.breakdown) {
        Object.entries(expenseBreakdown.breakdown).forEach(([paidBy, categories]) => {
            categories.forEach(cat => {
                expenseData.push({
                    name: `${paidBy}: ${cat.category}`,
                    value: cat.total,
                    percentage: cat.percentage
                });
            });
        });
    }

    // Chart 3: Charges vs Remittance per car
    const chargesVsRemittanceData = ownerReport?.cars?.map(car => ({
        name: car.plate_no,
        Remittance: parseFloat(car.wkr_total || 0),
        Charges: parseFloat(car.expenses_total || 0),
        NetIncome: parseFloat(car.net_income || 0)
    })) || [];

    // Chart 4: Historical trend
    const historicalData = trendData?.trend?.map(week => ({
        week: week.week_key,
        payout: week.total_payout,
        remittance: week.total_remittance,
        charges: week.total_charges
    })) || [];

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
                    <p className="font-semibold text-[#2a2e30] mb-2">{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }} className="text-sm">
                            {entry.name}: {formatCurrency(entry.value)}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const PieTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
                    <p className="font-semibold text-[#2a2e30] mb-1">{payload[0].name}</p>
                    <p className="text-sm" style={{ color: payload[0].payload.fill }}>
                        {formatCurrency(payload[0].value)}
                    </p>
                    <p className="text-xs text-gray-600">
                        {payload[0].payload.percentage.toFixed(1)}%
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="space-y-8">
            {/* Chart 1: Stacked Bar - Income Breakdown */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-[#2a2e30] mb-4">Income & Remittance Breakdown by Vehicle</h3>
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={incomeData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="name" stroke="#6b7280" />
                        <YAxis stroke="#6b7280" />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar dataKey="WKI" stackId="a" fill={COLORS.blue} name="Weekly Income (WKI)" />
                        <Bar dataKey="WKR" stackId="a" fill={COLORS.green} name="Weekly Remittance (WKR)" />
                        <Bar dataKey="Charges" fill={COLORS.red} name="Total Charges" />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* Chart 2: Pie - Expense Distribution */}
            {expenseData.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-xl font-bold text-[#2a2e30] mb-4">Expense Distribution by Category</h3>
                    <ResponsiveContainer width="100%" height={400}>
                        <PieChart>
                            <Pie
                                data={expenseData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percentage }) => `${name}: ${percentage.toFixed(1)}%`}
                                outerRadius={120}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {expenseData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={Object.values(COLORS)[index % Object.values(COLORS).length]} />
                                ))}
                            </Pie>
                            <Tooltip content={<PieTooltip />} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Chart 3: Bar - Charges vs Remittance */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-[#2a2e30] mb-4">Remittance vs Charges Comparison</h3>
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={chargesVsRemittanceData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="name" stroke="#6b7280" />
                        <YAxis stroke="#6b7280" />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar dataKey="Remittance" fill={COLORS.green} name="Total Remittance" />
                        <Bar dataKey="Charges" fill={COLORS.red} name="Total Charges" />
                        <Bar dataKey="NetIncome" fill={COLORS.primary} name="Net Income" />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* Chart 4: Line - Historical Trend */}
            {historicalData.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-xl font-bold text-[#2a2e30] mb-4">8-Week Historical Trend</h3>
                    <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={historicalData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="week" stroke="#6b7280" />
                            <YAxis stroke="#6b7280" />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Line
                                type="monotone"
                                dataKey="payout"
                                stroke={COLORS.primary}
                                strokeWidth={3}
                                name="Owner Payout"
                                dot={{ fill: COLORS.primary, r: 5 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="remittance"
                                stroke={COLORS.blue}
                                strokeWidth={2}
                                name="Total Remittance"
                                dot={{ fill: COLORS.blue, r: 4 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="charges"
                                stroke={COLORS.red}
                                strokeWidth={2}
                                name="Total Charges"
                                dot={{ fill: COLORS.red, r: 4 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

export default RemittanceFinancialCharts;
